import { useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../../contexts/AuthContext";
import { useQuery } from "react-query";
import { GuideDetails, WebinarContent } from "../../@types";
import {
  getCoachRecommendations,
  getTeaserContent,
  getUserGuide,
} from "../../services/guide.service";
import { CircularProgress } from "@mui/material";
import { CircularImage } from "../circularImage";
import { ReactComponent as RightArrow } from "../../assets/rigth-arrow_bigger.svg";
import { formatDateRange } from "../../utils";

export function TeaserDetails({
  className,
  onClose,
}: {
  className?: string;
  onClose?: () => void;
}) {
  const { t } = useTranslation("teaser");

  const navigate = useNavigate();
  const { user, configurations } = useContext(AuthContext);

  const productType = configurations?.productType;
  const isBLUE = productType === "BLUE";

  const includesGuide = configurations.includesGuide && !isBLUE;
  const includesCommunity = configurations.includesCommunity;

  const includesConsulting = configurations.includesGuide && isBLUE;

  const { data: guide, isLoading: guideLoading } = useQuery<GuideDetails>(
    ["guide"],
    getUserGuide,
    { retry: 0 }
  );

  const { data: teaserContent, isLoading: teaserContentLoading } =
    useQuery<any>(["teaserContent"], getTeaserContent, { retry: 0 });

  const { data: coachRecommendations, isLoading: coachRecommendationsLoading } =
    useQuery<any>(["coachRecommendations"], getCoachRecommendations, {
      retry: 0,
    });

  const handleClick = async () => {
    if (onClose) onClose();
    navigate(`/profile/guide`);
  };

  const handleClickConsulting = async () => {
    if (onClose) onClose();
    navigate(`/profile/consulting`);
  };

  const handleClickWebinar = async (webinarId: string) => {
    if (onClose) onClose();
    navigate(`/community/webinars/${webinarId}`);
  };

  const handleClickRecommendation = async () => {
    if (onClose) onClose();
    navigate(`/coaches`);
  };

  const isWithinNext7Days = (startDate: string) => {
    const today = new Date();
    const start = new Date(startDate);
    const differenceInTime = start.getTime() - today.getTime();
    const differenceInDays = differenceInTime / (1000 * 3600 * 24);
    return differenceInDays <= 7 && differenceInDays >= 0;
  };

  const wasVoucherUsedMoreThan24HoursAgo = (date: Date | undefined) => {
    if (!date) {
      return;
    }
    const usedAt = new Date(date);
    const now = new Date();
    const hoursDifference =
      (now.getTime() - usedAt.getTime()) / (1000 * 60 * 60);
    return hoursDifference > 24;
  };

  const shouldRender = () => {
    return (
      (wasVoucherUsedMoreThan24HoursAgo(user?.usedVoucher?.usedAt) &&
        (includesGuide || includesConsulting)) ||
      !(includesGuide || includesConsulting) ||
      (guide && guide?.meetingDates.length > 0)
    );
  };

  if (
    !(includesGuide || includesConsulting) &&
    !includesCommunity &&
    (!coachRecommendations ||
      coachRecommendations?.recommendedCategories.length === 0)
  ) {
    return null;
  }

  const hasRelevantTeaserContent = teaserContent?.some((webinar: any) => {
    return (
      webinar?.fields?.registered && isWithinNext7Days(webinar.fields.startDate)
    );
  });

  const isInTheFuture = (startDate: string) => {
    const now = new Date();
    const start = new Date(startDate);
    return start > now;
  };

  const hasUnregisteredTeaserContent = teaserContent?.some((webinar: any) => {
    return (
      !webinar?.fields?.registered && isInTheFuture(webinar.fields.startDate)
    );
  });
  const formatCategoryNames = (categories: any) => {
    if (!categories || !Array.isArray(categories) || categories.length === 0)
      return "";

    const titles = categories.map((category: any) => category.title);

    if (titles.length === 1) return `"${titles[0]}"`;
    if (titles.length === 2) return `"${titles[0]}" ${t("or")} "${titles[1]}"`;

    const lastCategory = titles[titles.length - 1];
    const otherCategories = titles.slice(0, -1);

    return `"${otherCategories.join(", ")}", ${t("or")} "${lastCategory}"`;
  };

  // Usage with coachRecommendations
  const recommendedCategoryNames = formatCategoryNames(
    coachRecommendations?.recommendedCategories
  );

  if (
    (!guide || guide.meetingDates.length > 0) &&
    (!hasUnregisteredTeaserContent || !includesCommunity) &&
    (!hasRelevantTeaserContent || !includesCommunity) &&
    (!coachRecommendations ||
      coachRecommendations?.recommendedCategories.length === 0)
  ) {
    return null;
  }

  return (
    <>
      <div
        className={`flex flex-col w-full p-6 text-Yellow-light bg-white lg:bg-Yellow-super-light text-black rounded-lg ${className}`}
      >
        <h3 className="mb-4 hidden font-medium lg:flex">
          {t("hello")} {user?.userDetails.firstName}!
        </h3>
        <h3
          className="lg:hidden text-[14px] font-[500]"
          style={{ marginBottom: "7px" }}
        >
          {t("hello")} {user?.userDetails.firstName}!
        </h3>

        <h2 className="font-semibold hidden lg:flex text-xl">
          {t("recommendations")}
        </h2>
        <div className="font-semibold lg:hidden text-[16px] font-[700]">
          {t("recommendations")}
        </div>

        {guide && includesConsulting && guide.meetingDates.length === 0 && (
          <>
            <hr className="h-[1px] bg-[#A7B6C5] lg:bg-Gray-dark border-0 mt-3" />

            <div className="flex justify-between items-center">
              <div className=" font-medium flex">{t("guide-title")}</div>
            </div>
            <div onClick={handleClickConsulting} className="cursor-pointer">
              <div className="flex mt-4">
                <div className="flex flex-col items-center w-1/7">
                  <div className="w-[48px] h-[48px] overflow-hidden">
                    <CircularImage src={`https:${guide?.profilePicture}`} />
                  </div>
                </div>

                <div className="flex flex-col justify-center w-6/7 ml-4">
                  <h1 className="text-md font-bold hidden lg:block">
                    {`${guide?.givenName} ${guide?.surname}`}
                  </h1>
                  <div
                    className="font-[700] text-[14px] lg:hidden"
                    style={{ marginTop: "11px" }}
                  >
                    {`${guide?.givenName} ${guide?.surname}`}
                  </div>
                  <h2 className="mt-1 font-medium hidden lg:block">
                    {t("info-consulting")}
                  </h2>
                  <h2 className="mt-1 font-[500] text-[14px] lg:hidden">
                    {t("info-consulting")}
                  </h2>
                </div>
              </div>

              <div className="flex justify-end items-center mt-2">
                <div className="mr-5 hidden lg:flex font-semibold text-gray-500 text-left">
                  {t("schedule-guide-consulting")}
                </div>
                <div className="mr-5 flex lg:hidden text-[14px] text-[#6C829B] font-[700] text-right">
                  {t("schedule-guide-consulting")} &gt;
                </div>

                <div className="hidden lg:flex">
                  <RightArrow />
                </div>
              </div>
            </div>
          </>
        )}

        {guide && includesGuide && guide.meetingDates.length === 0 && (
          <>
            <hr className="h-[1px] bg-[#A7B6C5] lg:bg-Gray-dark border-0 mt-3" />

            <div className="flex justify-between items-center">
              <div className=" font-medium flex">{t("guide-title")}</div>
            </div>
            <div onClick={handleClick} className="cursor-pointer">
              <div className="flex mt-4">
                <div className="flex flex-col items-center w-1/7">
                  <div className="w-[48px] h-[48px] overflow-hidden">
                    <CircularImage src={`https:${guide?.profilePicture}`} />
                  </div>
                </div>

                <div className="flex flex-col justify-center w-6/7 ml-4">
                  <h1 className="text-md font-bold hidden lg:block">
                    {`${guide?.givenName} ${guide?.surname}`}
                  </h1>
                  <div
                    className="font-[700] text-[14px] lg:hidden"
                    style={{ marginTop: "11px" }}
                  >
                    {`${guide?.givenName} ${guide?.surname}`}
                  </div>
                  <h2 className="mt-1 font-medium hidden lg:block">
                    {t("info")}
                  </h2>
                  <h2 className="mt-1 font-[500] text-[14px] lg:hidden">
                    {t("info")}
                  </h2>
                </div>
              </div>

              <div className="flex justify-end items-center mt-2">
                <div className="mr-5 hidden lg:flex font-semibold text-gray-500 text-left">
                  {t("schedule-guide")}
                </div>
                <div className="mr-5 flex lg:hidden text-[14px] text-[#6C829B] font-[700] text-right">
                  {t("schedule-guide")} &gt;
                </div>

                <div className="hidden lg:flex">
                  <RightArrow />
                </div>
              </div>
            </div>
          </>
        )}
        {teaserContent &&
          shouldRender() &&
          includesCommunity &&
          teaserContent.map((webinar: any, index: number) => (
            <>
              {hasUnregisteredTeaserContent || hasRelevantTeaserContent ? (
                <>
                  <hr className="h-[1px] bg-[#A7B6C5] lg:bg-Gray-dark border-0 mt-6" />

                  <div className="lg:mt-4">
                    {" "}
                    <h2 className="font-bold text-md hidden lg:block">
                      {t("webinar-teaser-title")}
                    </h2>
                    <div
                      className="font-[700] text-[14px] lg:hidden"
                      style={{ marginTop: "12px" }}
                    >
                      {t("webinar-teaser-title")}
                    </div>
                  </div>

                  <div
                    onClick={() => handleClickWebinar(webinar.sys.id)}
                    className="cursor-pointer"
                  >
                    {webinar.fields.registered ? (
                      <>
                        <div className="mt-2 font-medium hidden lg:flex flex-col">
                          <div>{`${t("7days-webinar")} ${
                            webinar.fields.title
                          }`}</div>
                          <div className="mt-2">
                            {formatDateRange(
                              webinar.fields.startDate,
                              webinar.fields.endDate
                            )}
                          </div>
                          <div className="flex justify-end items-center">
                            <div className="ml-5 mb-2">
                              <RightArrow />
                            </div>
                          </div>
                        </div>

                        {/* MOBILE SM + MD */}
                        <div className="font-[500] text-[14px] lg:hidden flex flex-col">
                          <div className="" style={{ marginTop: "11px" }}>{`${t(
                            "7days-webinar"
                          )} ${webinar.fields.title}`}</div>
                          <div className="" style={{ marginTop: "11px" }}>
                            {formatDateRange(
                              webinar.fields.startDate,
                              webinar.fields.endDate
                            )}
                          </div>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="mt-2 font-medium hidden lg:flex flex-col">
                          <div>{`${webinar.fields.title}`}</div>
                          <div className="mt-2">
                            {formatDateRange(
                              webinar.fields.startDate,
                              webinar.fields.endDate
                            )}
                          </div>
                          <div className="flex justify-end items-center">
                            <div className="mr-5 mt-2 font-semibold text-gray-500 text-left">
                              {t("register-webinar")}
                            </div>
                            <div className="mt-2">
                              <RightArrow />
                            </div>
                          </div>
                        </div>

                        <div className="font-[500] text-[14px] lg:hidden flex flex-col">
                          <div
                            style={{ marginTop: "11px" }}
                          >{`${webinar.fields.title}`}</div>
                          <div className="" style={{ marginTop: "11px" }}>
                            {formatDateRange(
                              webinar.fields.startDate,
                              webinar.fields.endDate
                            )}
                          </div>
                          <div className="flex justify-end items-center mb-3">
                            <div
                              className="mr-5 flex lg:hidden text-[14px] text-[#6C829B] font-[700] text-right"
                              style={{ marginTop: "11px" }}
                            >
                              {t("register-webinar")} &gt;
                            </div>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </>
              ) : null}
            </>
          ))}
        {coachRecommendations &&
          shouldRender() &&
          coachRecommendations?.recommendedCategories.length > 0 && (
            <>
              <hr className="h-[1px] bg-[#A7B6C5] lg:bg-Gray-dark border-0 mt-3" />
              <div className="lg:mt-4">
                {" "}
                <h2 className="font-bold text-md hidden lg:block">
                  {t("recommendation-title")}
                </h2>
                <div
                  className="font-[700] text-[14px] lg:hidden"
                  style={{ marginTop: "12px" }}
                >
                  {t("recommendation-title")}
                </div>
              </div>
              <div className="lg:block hidden mt-2 ">
                {t("recommendation-teaser-text")}{" "}
                {recommendedCategoryNames && (
                  <span>{recommendedCategoryNames}</span>
                )}
              </div>
              <div className="lg:hidden mt-2 font-[500] text-[14px]">
                {t("recommendation-teaser-text")}{" "}
                {recommendedCategoryNames && (
                  <span>{recommendedCategoryNames}</span>
                )}
              </div>
              <div className="flex justify-start items-center mt-2">
                <div className="flex">
                  {coachRecommendations?.recommendedCoaches
                    ?.slice(0, 3)
                    .map((coach: any) => (
                      <div
                        key={coach?.coach?.sys?.id}
                        className="w-[48px] h-[48px] m-2 overflow-hidden cursor-pointer"
                        onClick={() => {
                          if (onClose) onClose();
                          navigate(
                            `/coaches/${coach?.coach?.sys?.id}/overview`
                          );
                        }}
                      >
                        <CircularImage
                          src={`https:${coach?.coach?.fields?.profilePicture?.fields?.file?.url}`}
                        />
                      </div>
                    ))}
                </div>
              </div>
              <div
                className="lg:block hidden lg:flex justify-end items-center cursor-pointer"
                onClick={() => handleClickRecommendation()}
              >
                <div className="mr-5 font-semibold text-gray-500 text-left">
                  {t("recommendation-all")}
                </div>
                <div className="">
                  <RightArrow />
                </div>
              </div>
              <div className="lg:hidden flex justify-end items-center">
                <div
                  className="mr-5 flex lg:hidden text-[14px] text-[#6C829B] font-[700] text-right cursor-pointer"
                  onClick={() => handleClickRecommendation()}
                >
                  {t("recommendation-all")} &gt;
                </div>
              </div>
            </>
          )}
      </div>
      <div className="my-12 lg:hidden ">
        <div style={{ background: "#A7B6C5", height: "1px" }} />
      </div>
    </>
  );
}
